import { platformApiClient } from './config'
import type { ApiResponse } from './types'



export interface FileUploadResponse {
  fileCode: string
  fileName: string
  fileSize: number
  fileType: string
  fileUrl?: string
  uploadTime?: string
  localFilePath?: string
  cloudStorageId?: string | null
  storageProvider?: string | null
  fileHash?: string
}

export interface FileInfo {
  fileCode: string
  fileName: string
  fileSize: number
  fileType: string
  fileUrl: string
  uploadTime: string
  heatLevel: number
  brandId?: number
  storageType?: string
  formatStatus?: number
  previewUrl?: string
  createdAt?: string
  updatedAt?: string
}

// 文件URL响应接口
export interface FileUrlResponse {
  fileUrl: string
  storageType: string
  fileCode: string
  fileName: string
  fileSize: number
  fileType: string
  formatStatus: number
  preview?: string
}

export interface FileSearchParams {
  keyword?: string
  fileType?: string
  heatLevel?: number
  page?: number
  pageSize?: number
}

export interface FileSearchResponse {
  records: FileInfo[]
  totalCount: number
  page: number
  pageSize: number
}

export interface ChunkUploadInitRequest {
  FileName: string // 改为大写开头
  FileSize: number // 改为大写开头
  FileHash: string // 改为大写开头
  ChunkSize: number // 改为大写开头
  BrandId?: number // 改为BrandId
}

export interface ChunkUploadInitResponse {
  uploadSessionId: string // 使用camelCase，匹配服务器返回
  fileCode: string // 使用camelCase
  totalChunks: number // 使用camelCase
  chunkSize: number // 使用camelCase
  uploadedChunks: number[] // 使用camelCase
}

export interface ChunkUploadRequest {
  UploadSessionId: string // 改为大写开头，匹配C#模型
  ChunkIndex: number // 改为大写开头
  ChunkData: string // 改为string，使用Base64编码
  ChunkHash: string // 改为大写开头
}

export interface ChunkUploadResponse {
  sessionId: string
  uploadedChunks: number[]
  isCompleted: boolean
  fileCode?: string
}

export interface ChunkSessionInfo {
  sessionId: string
  fileName: string
  fileSize: number
  totalChunks: number
  uploadedChunks: number[]
  isCompleted: boolean
  fileCode?: string
  createdAt: string
  lastActivity: string
}

// 文件存储API类
export class FileStorageApi {
  // 请求缓存：用于去重相同 fileCode 的请求
  private static fileUrlCache = new Map<string, Promise<ApiResponse<FileUrlResponse>>>()

  // 上传文件
  static async uploadFile(
    file: File, 
    onProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<FileUploadResponse>> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await platformApiClient.post(`/api/FileStorage/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onProgress,
      timeout: 300000, // 5分钟超时
      maxContentLength: Infinity,
      maxBodyLength: Infinity
    })
    return response.data
  }

  // 下载文件
  static async downloadFile(fileCode: string): Promise<Blob> {
    const response = await platformApiClient.get(`/api/FileStorage/download/${fileCode}`, {
      responseType: 'blob',
    })
    return response.data
  }

  // 获取文件信息
  static async getFileInfo(fileCode: string): Promise<ApiResponse<FileInfo>> {
    const response = await platformApiClient.get(`/api/FileStorage/info/${fileCode}`)
    return response.data
  }

  // 获取文件URL（带请求去重）
  static async getFileUrl(fileCode: string): Promise<ApiResponse<FileUrlResponse>> {
    // 如果已经有相同的请求在进行中，直接返回该请求的 Promise
    if (this.fileUrlCache.has(fileCode)) {
      return this.fileUrlCache.get(fileCode)!
    }
    
    // 创建新请求并缓存
    const requestPromise = platformApiClient.get(`/api/FileStorage/url/${fileCode}`)
      .then(response => {
        // 请求完成后从缓存中移除
        this.fileUrlCache.delete(fileCode)
        return response.data
      })
      .catch(error => {
        // 请求失败也从缓存中移除，允许重试
        this.fileUrlCache.delete(fileCode)
        throw error
      })
    
    // 将请求 Promise 存入缓存
    this.fileUrlCache.set(fileCode, requestPromise)
    return requestPromise
  }

  // 删除文件
  static async deleteFile(fileCode: string): Promise<ApiResponse<boolean>> {
    const response = await platformApiClient.delete(`/api/FileStorage/delete/${fileCode}`)
    return response.data
  }

  // 搜索文件
  static async searchFiles(params: FileSearchParams): Promise<ApiResponse<FileSearchResponse>> {
    const queryParams = new URLSearchParams()
    if (params.keyword) queryParams.append('keyword', params.keyword)
    if (params.fileType) queryParams.append('fileType', params.fileType)
    if (params.heatLevel !== undefined) queryParams.append('heatLevel', params.heatLevel.toString())
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString())

    const response = await platformApiClient.get(`/api/FileStorage/search?${queryParams.toString()}`)
    return response.data
  }

  // 获取品牌文件列表
  static async getBrandFiles(brandId: number, page = 1, pageSize = 20): Promise<ApiResponse<FileSearchResponse>> {
    const queryParams = new URLSearchParams()
    queryParams.append('page', page.toString())
    queryParams.append('pageSize', pageSize.toString())

    const response = await platformApiClient.get(`/api/FileStorage/brand/${brandId}?${queryParams.toString()}`)
    return response.data
  }

  // 获取当前用户的品牌文件列表
  static async getMyBrandFiles(page = 1, pageSize = 20, fileCategories?: string): Promise<ApiResponse<FileSearchResponse>> {
    const queryParams = new URLSearchParams()
    queryParams.append('Page', page.toString())
    queryParams.append('PageSize', pageSize.toString())
    
    // 添加文件分类筛选参数
    if (fileCategories) {
      queryParams.append('FileCategories', fileCategories)
    }

    const url = `/api/FileStorage/my-brand?${queryParams.toString()}`
    console.log('请求URL:', url)
    
    const response = await platformApiClient.get(url)
    return response.data
  }

  // 获取已上传文件列表
  static async getUploadedFiles(page = 1, pageSize = 20, brandId?: number): Promise<ApiResponse<any>> {
    const queryParams = new URLSearchParams()
    queryParams.append('page', page.toString())
    queryParams.append('pageSize', pageSize.toString())
    if (brandId) {
      queryParams.append('brandId', brandId.toString())
    }

    const response = await platformApiClient.get(`/api/FileStorage/uploaded?${queryParams.toString()}`)
    return response.data
  }

  // 初始化分块上传
  static async initChunkUpload(request: ChunkUploadInitRequest): Promise<ApiResponse<ChunkUploadInitResponse>> {
    const response = await platformApiClient.post(`/api/FileStorage/chunk/init`, request)
    return response.data
  }

  // 上传分块（使用 FormData 方式）
  static async uploadChunk(request: ChunkUploadRequest, chunkBlob: Blob): Promise<ApiResponse<ChunkUploadResponse>> {
    const formData = new FormData()
    formData.append('uploadSessionId', request.UploadSessionId)
    formData.append('chunkIndex', request.ChunkIndex.toString())
    formData.append('chunkFile', chunkBlob)
    
    const response = await platformApiClient.post(`/api/FileStorage/chunk/upload-v2`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5分钟超时
    })
    return response.data
  }

  // 获取分块上传会话信息
  static async getChunkSession(sessionId: string): Promise<ApiResponse<ChunkSessionInfo>> {
    const response = await platformApiClient.get(`/api/FileStorage/chunk/session/${sessionId}`)
    return response.data
  }

  // 取消分块上传
  static async cancelChunkUpload(sessionId: string): Promise<ApiResponse<boolean>> {
    const response = await platformApiClient.delete(`/api/FileStorage/chunk/cancel/${sessionId}`)
    return response.data
  }

  // 恢复分块上传
  static async resumeChunkUpload(sessionId: string): Promise<ApiResponse<ChunkSessionInfo>> {
    const response = await platformApiClient.get(`/api/FileStorage/chunk/resume/${sessionId}`)
    return response.data
  }
}

// 文件上传工具类
export class FileUploadUtils {
  // 计算文件哈希 - 使用SHA256与服务器端保持一致
  static async calculateFileHash(file: File): Promise<string> {
    // 始终使用完整文件内容计算SHA256哈希，与服务器端保持一致
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = async (e) => {
        const arrayBuffer = e.target?.result as ArrayBuffer
        // 使用Web Crypto API计算SHA256哈希
        const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        // 转换为Base64编码，匹配服务器端格式
        const binaryString = String.fromCharCode.apply(null, hashArray)
        const hash = btoa(binaryString)
        resolve(hash)
      }
      reader.readAsArrayBuffer(file)
    })
  }

  // 分块文件 - 优化版本
  static async chunkFile(file: File, chunkSize: number): Promise<ArrayBuffer[]> {
    const chunks: ArrayBuffer[] = []
    const totalChunks = Math.ceil(file.size / chunkSize)

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize
      const end = Math.min(start + chunkSize, file.size)
      const chunk = file.slice(start, end)
      
      try {
        const arrayBuffer = await chunk.arrayBuffer()
        chunks.push(arrayBuffer)
      } catch (error) {
        console.error(`分片 ${i + 1} 失败:`, error)
        throw error
      }
    }

    return chunks
  }

  // 计算分块哈希 - 使用SHA256与服务器端保持一致
  static async calculateChunkHash(chunk: ArrayBuffer): Promise<string> {
    // 使用Web Crypto API计算SHA256哈希
    const hashBuffer = await crypto.subtle.digest('SHA-256', chunk)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    // 转换为Base64编码，匹配服务器端格式
    const binaryString = String.fromCharCode.apply(null, hashArray)
    return btoa(binaryString)
  }
}

// 导出默认实例
export default FileStorageApi